import { useState } from 'react'
import { useMutation, useApolloClient, gql } from '@apollo/client'
import { Formik } from 'formik'
import { useIntl, defineMessages, FormattedMessage } from 'react-intl'

import { getUnprocessableErrors } from 'utils/error'
import { Input } from 'components/Input'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { resolvers as bookingsResolvers, getUnpaidTicketsForPayment } from 'resolvers/bookings'
import { schema } from './schema'
import {
  GTBookingV2PaymentMethod,
  GTBookingV2PaymentProcessor,
  GTPaymentMethod,
  GTPaymentProcessor
} from 'gql/types.generated'

const messages = defineMessages({
  chargeIdPlaceholder: {
    id: 'bookings.chargeId.placeholder',
    defaultMessage: 'Last 4 or charge Id'
  }
})

const CONFIRM_BOOKING_PAYMENT_ON_SITE = gql`
  mutation ConfirmBookingPaymentOnSite(
    $bookingId: ID!
    $chargeId: String
    $last4: String
    $method: PaymentMethod!
    $processor: PaymentProcessor
    $ticketIds: [ID!]!
  ) {
    confirmBookingPaymentOnSite(
      input: {
        bookingID: $bookingId
        chargeID: $chargeId
        last4: $last4
        method: $method
        processor: $processor
        ticketIDs: $ticketIds
      }
    )
  }
`

const UPDATE_BOOKING_PAYMENT = gql`
  mutation UpdateBookingPayment(
    $bookingId: ID!
    $chargeId: String
    $last4: String
    $method: GTBookingV2PaymentMethod!
    $processor: GTBookingV2PaymentProcessor!
    $tickets: [Ticket!]!
  ) {
    updateBookingPayment(
      bookingId: $bookingId
      chargeId: $chargeId
      last4: $last4
      method: $method
      processor: $processor
      tickets: $tickets
    ) @client
  }
`

const isLast4 = (str: TSFixMe): TSFixMe => {
  return str.length === 4
}

export const BookingAddChargeId = ({ booking }: TSFixMe): TSFixMe => {
  const client = useApolloClient()
  // @ts-expect-error ts(2322) FIXME
  client.addResolvers(bookingsResolvers)

  const [chargeIdSent, setChargeIdSent] = useState()
  const intl = useIntl()

  const [updateBookingPayment] = useMutation(UPDATE_BOOKING_PAYMENT)
  const [confirmBookingPaymentOnSite, { loading, error }] = useMutation(CONFIRM_BOOKING_PAYMENT_ON_SITE, {
    onCompleted() {
      const unpaidTickets = getUnpaidTicketsForPayment(booking)

      let payload = {
        bookingId: booking.id,
        method: GTBookingV2PaymentMethod.Card,
        tickets: unpaidTickets
      }

      if (isLast4(chargeIdSent)) {
        payload = {
          ...payload,
          /*@ts-expect-error ts(2322) FIXME*/
          last4: chargeIdSent,
          processor: GTBookingV2PaymentProcessor.Clip
        }
      } else {
        payload = {
          ...payload,
          /*@ts-expect-error ts(2322) FIXME*/

          chargeId: chargeIdSent,
          processor: GTBookingV2PaymentProcessor.Conekta
        }
      }

      updateBookingPayment({
        variables: payload
      })
    }
  })

  const handleSubmit = (values: TSFixMe): TSFixMe => {
    const unpaidTickets = getUnpaidTicketsForPayment(booking)
    setChargeIdSent(values.chargeId)
    let payload = {
      bookingId: booking.id,
      method: GTPaymentMethod.Card,
      /*@ts-expect-error ts(2322) FIXME*/
      ticketIds: unpaidTickets.map(({ id }): TSFixMe => id)
    }

    if (isLast4(values.chargeId)) {
      payload = {
        ...payload,
        /*@ts-expect-error ts(2322) FIXME*/
        last4: values.chargeId,
        processor: GTPaymentProcessor.Clip
      }
    } else {
      payload = {
        ...payload,
        /*@ts-expect-error ts(2322) FIXME*/
        chargeId: values.chargeId,
        processor: GTPaymentProcessor.Conekta
      }
    }

    confirmBookingPaymentOnSite({
      variables: payload
    })
  }

  const initialValues = {
    chargeId: ''
  }

  return (
    <Formik initialValues={initialValues} validationSchema={schema(intl)} onSubmit={handleSubmit}>
      {(props): TSFixMe => {
        const { touched, errors, handleChange, handleSubmit } = props

        return (
          <form onSubmit={handleSubmit}>
            <Input
              id="chargeId"
              value={props.values.chargeId}
              onChange={handleChange}
              placeholder={intl.formatMessage(messages.chargeIdPlaceholder)}
              touched={touched.chargeId}
              errors={errors.chargeId}
              small
              autoFocus
              disabled={loading}
              loading={loading}
            />

            {error && isLast4(chargeIdSent) && (
              <ErrorMessage errors={getUnprocessableErrors(error)}>
                <FormattedMessage
                  id="bookings.last4.error"
                  defaultMessage="There was an error while adding the last 4 digits"
                />
              </ErrorMessage>
            )}

            {error && !isLast4(chargeIdSent) && (
              <ErrorMessage errors={getUnprocessableErrors(error)}>
                <FormattedMessage
                  id="bookings.chargeId.error"
                  defaultMessage="There was an error while adding the charge id"
                />
              </ErrorMessage>
            )}
          </form>
        )
      }}
    </Formik>
  )
}
